import { createContext, PropsWithChildren, useContext } from 'react';

import { SvgIcon } from '@mui/material';
import MuiCheckbox, {
  CheckboxProps as MuiCheckboxProps,
} from '@mui/material/Checkbox';
import clsx from 'clsx';
import { Check } from 'phosphor-react';

import { WithRef } from '../../../types';

import {
  ControlLabelSettings,
  useControlLabelSettings,
} from '../ControlLabelContext';

export type CheckboxProps = Omit<MuiCheckboxProps, 'color'> & {
  error?: boolean;
  testId?: string;
};

export const CheckboxContext = createContext<CheckboxProps | undefined>(
  undefined
);

const CheckboxBorder = ({
  children,
  fontSize,
}: PropsWithChildren<{ fontSize?: 'small' | 'medium' }>) => {
  return (
    <div
      className={clsx('MuiCheckbox-body', `MuiCheckbox-fontSize-${fontSize}`)}
    >
      {children}
    </div>
  );
};

const Checkmark = () => {
  return (
    <Check
      className="MuiCheckbox-checkmark"
      color="currentColor"
      weight="bold"
    />
  );
};

const Indeterminate = () => {
  return (
    <SvgIcon className="MuiCheckbox-checkmark" viewBox="0 0 8 2">
      <rect width="8" height="2" rx="1" fill="currentColor" />
    </SvgIcon>
  );
};

/**
 * Maps ControlLabelContext settings into Checkbox props.
 *
 * Merge order: defaults < CheckboxContext < board < own props.
 * - required: emit `required` + `aria-required` (MUI routes `required` to the
 *   native input, `aria-required` to the root — same DOM as before).
 * - disabled: maps only board `true` (a board `false` would shadow a
 *   CheckboxContext `true`).
 * - error: not spread (not a MUI prop — would leak to the DOM); feeds color only.
 */
const mapCheckboxBoard = (
  cl?: ControlLabelSettings
): Partial<CheckboxProps> & { 'aria-required'?: boolean } => {
  if (!cl) return {};
  const board: Partial<CheckboxProps> & { 'aria-required'?: boolean } = {};
  // Size mapping mirrors the board broadcast: ControlLabelSize sm/md -> MUI small/medium.
  if (cl.size === 'sm') board.size = 'small';
  if (cl.size === 'md') board.size = 'medium';
  // The label's `for` points at this id — MUI routes `id` to the native input.
  if (cl.id) board.id = cl.id;
  if (cl.disabled === true) board.disabled = true;
  if (cl.required === true) {
    board.required = true;
    board['aria-required'] = true;
  }
  return board;
};

const Checkbox = ({
  ref,
  error,
  testId,
  ...props
}: WithRef<HTMLButtonElement, CheckboxProps>) => {
  const checkBoxCtx = useContext(CheckboxContext);
  const cl = useControlLabelSettings();
  const board = mapCheckboxBoard(cl);

  // own error > CheckboxContext > board (?? keeps explicit false).
  const resolvedError = error ?? checkBoxCtx?.error ?? cl?.error;

  return (
    <MuiCheckbox
      data-testid={testId}
      {...checkBoxCtx}
      {...board}
      {...props}
      color={resolvedError ? 'error' : 'primary'}
      icon={<CheckboxBorder />}
      checkedIcon={
        <CheckboxBorder>
          <Checkmark />
        </CheckboxBorder>
      }
      indeterminateIcon={
        <CheckboxBorder>
          <Indeterminate />
        </CheckboxBorder>
      }
      ref={ref}
    />
  );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
